import { useState } from "react";
import * as Sentry from "@sentry/react";
import { useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import {
  useLazyGetVehiclesByYearAndMakeQuery,
  useLazyGetVehiclesByYearMakeAndModelQuery,
  useLazyGetVehiclesByYearQuery,
  useLazyGetVehiclesYearsQuery,
} from "@api/catalogs";
import { VehicleStrutureCatalog } from "@interfaces/catalogs";
import { ApiError } from "@interfaces/response";
import { LeasingVehicleDetailsPost } from "@interfaces/LeasingTypes";
import { AssetVehicleValues } from "@interfaces/seleccionActivo";

export type ChainedCatalog = "brand" | "model" | "version";

/** Busca la opción por el nombre guardado (sin importar mayúsculas ni espacios) o por su código. */
const findCatalogItem = (options: VehicleStrutureCatalog[], target: string | number | undefined) => {
  const normalize = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();
  const needle = normalize(String(target ?? ""));
  return (
    options.find((item) => normalize(item.name) === needle) ?? options.find((item) => String(item.code) === needle)
  );
};

const nameOf = (options: VehicleStrutureCatalog[], code: string) =>
  options.find((item) => item.code === code)?.name ?? "";

/**
 * Libro Azul de un solo vehículo (`SeleccionActivo`): años y los catálogos encadenados año → marca → modelo → versión.
 * Cada `load*` limpia lo que depende de él y carga el siguiente; si un catálogo falla, queda vacío y se abre el modal
 * de error. El formulario guarda los códigos; `names` da lo que se manda al backend.
 */
const useLibroAzul = () => {
  const dispatch = useAppDispatch();
  const [triggerYears] = useLazyGetVehiclesYearsQuery();
  const [triggerBrands] = useLazyGetVehiclesByYearQuery();
  const [triggerModels] = useLazyGetVehiclesByYearAndMakeQuery();
  const [triggerVersions] = useLazyGetVehiclesByYearMakeAndModelQuery();
  const [years, setYears] = useState<string[]>([]);
  const [brands, setBrands] = useState<VehicleStrutureCatalog[]>([]);
  const [models, setModels] = useState<VehicleStrutureCatalog[]>([]);
  const [versions, setVersions] = useState<VehicleStrutureCatalog[]>([]);
  /** Catálogo que se está cargando: su select muestra el spinner. */
  const [loading, setLoading] = useState<ChainedCatalog | null>(null);

  const report = (error: unknown, catalog: string) => {
    Sentry.captureException(error, { extra: { component: `useLibroAzul-${catalog}` } });
    dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
  };

  const load = async (
    catalog: ChainedCatalog,
    request: () => Promise<VehicleStrutureCatalog[]>,
    setCatalog: (items: VehicleStrutureCatalog[]) => void,
  ) => {
    setLoading(catalog);
    try {
      setCatalog(await request());
    } catch (error) {
      setCatalog([]);
      report(error, catalog);
    } finally {
      setLoading(null);
    }
  };

  /** Los años se piden una sola vez. */
  const loadYears = () => {
    if (years.length > 0) return;
    triggerYears()
      .unwrap()
      .then(setYears)
      .catch((error) => report(error, "years"));
  };

  const loadBrands = (year: string) => {
    setModels([]);
    setVersions([]);
    if (!year) return setBrands([]);
    load("brand", () => triggerBrands({ year }).unwrap(), setBrands);
  };

  const loadModels = (year: string, brand: string) => {
    setVersions([]);
    if (!brand) return setModels([]);
    load("model", () => triggerModels({ year, make: brand }).unwrap(), setModels);
  };

  const loadVersions = (year: string, brand: string, model: string) => {
    if (!model) return setVersions([]);
    load("version", () => triggerVersions({ year, make: brand, model }).unwrap(), setVersions);
  };

  const reset = () => {
    setBrands([]);
    setModels([]);
    setVersions([]);
  };

  /**
   * Del vehículo guardado (nombres) a los códigos del formulario: vuelve a cargar los catálogos hasta la versión. Los
   * errores se lanzan a la pantalla, que es la que maneja la carga inicial.
   */
  const hydrate = async (details: LeasingVehicleDetailsPost): Promise<AssetVehicleValues> => {
    const year = details.year ? String(details.year) : "";
    const empty = { year, brand: "", model: "", version: "" };
    if (!year) return empty;
    const brandList = await triggerBrands({ year }).unwrap();
    setBrands(brandList);
    const brand = findCatalogItem(brandList, details.make);
    if (!brand) return empty;
    const modelList = await triggerModels({ year, make: brand.code }).unwrap();
    setModels(modelList);
    const model = findCatalogItem(modelList, details.model);
    if (!model) return { ...empty, brand: brand.code };
    const versionList = await triggerVersions({ year, make: brand.code, model: model.code }).unwrap();
    setVersions(versionList);
    const version = findCatalogItem(versionList, details.car_version_code || details.version);
    return { year, brand: brand.code, model: model.code, version: version?.code ?? "" };
  };

  /** Nombres de marca, modelo y versión elegidos; al backend van los nombres, como en el arrendamiento original. */
  const names = (values: AssetVehicleValues) => ({
    make: nameOf(brands, values.brand),
    model: nameOf(models, values.model),
    version: nameOf(versions, values.version),
  });

  return {
    years,
    brands,
    models,
    versions,
    loading,
    loadYears,
    loadBrands,
    loadModels,
    loadVersions,
    reset,
    hydrate,
    names,
  };
};

export type LibroAzul = ReturnType<typeof useLibroAzul>;

export default useLibroAzul;
